import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withCtx as _withCtx, Teleport as _Teleport, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mb-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between" }
const _hoisted_2 = { class: "max-w-3xl px-4 xl:px-0" }
const _hoisted_3 = { class: "mb-2" }
const _hoisted_4 = { class: "" }
const _hoisted_5 = { class: "flex gap-2 px-4 xs:gap-3 xl:px-0" }
const _hoisted_6 = { class: "flex items-center" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", { class: "text-secondary mr-1 inline text-sm" }, "My unallocated votes", -1)
const _hoisted_8 = { key: 0 }
const _hoisted_9 = {
  key: 1,
  class: "mr-1"
}
const _hoisted_10 = { class: "flex items-center" }
const _hoisted_11 = { class: "text-lg font-semibold tabular-nums" }

import { computed, ref } from 'vue';

import useExpiredGaugesQuery from '@/composables/queries/useExpiredGaugesQuery';
import useVeBalLockInfoQuery from '@/composables/queries/useVeBalLockInfoQuery';
import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import { orderedPoolTokens, poolURLFor } from '@/composables/usePool';
import useVotingGauges from '@/composables/useVotingGauges';
import { bnum, scale } from '@/lib/utils';
import { VotingGaugeWithVotes } from '@/services/balancer/gauges/gauge-controller.decorator';

import GaugesTable from './GaugesTable.vue';
import GaugeVoteModal from './GaugeVoteModal.vue';

/**
 * DATA
 */

export default _defineComponent({
  setup(__props) {

const activeVotingGauge = ref<VotingGaugeWithVotes | null>(null);

/**
 * COMPOSABLES
 */
const {
  isLoading,
  votingGauges,
  unallocatedVotes,
  votingPeriodEnd,
  votingPeriodLastHour,
  refetch: refetchVotingGauges,
} = useVotingGauges();
const { fNum2 } = useNumbers();
const veBalLockInfoQuery = useVeBalLockInfoQuery();

const votingGaugeAddresses = computed<string[]>(
  () => votingGauges.value?.map(gauge => gauge.address) || []
);

const { data: expiredGauges } = useExpiredGaugesQuery(votingGaugeAddresses);

/**
 * COMPUTED
 */
const unallocatedVotesFormatted = computed<string>(() =>
  fNum2(scale(bnum(unallocatedVotes.value), -4).toString(), FNumFormats.percent)
);

const unallocatedVoteWeight = computed(() => {
  const totalVotes = 1e4;
  if (isLoading.value || !votingGauges.value) return totalVotes;

  const votesRemaining = votingGauges.value.reduce((remainingVotes, gauge) => {
    return remainingVotes - parseFloat(gauge.userVotes);
  }, totalVotes);
  return votesRemaining;
});

const hasLock = computed(
  (): boolean =>
    !!veBalLockInfoQuery.data.value?.hasExistingLock &&
    !veBalLockInfoQuery.data.value?.isExpired
);

const hasExpiredLock = computed(
  (): boolean =>
    !!veBalLockInfoQuery.data.value?.hasExistingLock &&
    veBalLockInfoQuery.data.value?.isExpired
);

/**
 * METHODS
 */
function setActiveGaugeVote(votingGauge: VotingGaugeWithVotes) {
  activeVotingGauge.value = votingGauge;
}

function orderedTokenURIs(gauge: VotingGaugeWithVotes): string[] {
  const sortedTokens = orderedPoolTokens(
    gauge.pool.poolType,
    gauge.pool.address,
    gauge.pool.tokens
  );
  return sortedTokens.map(
    token => gauge.tokenLogoURIs[token?.address || ''] || ''
  );
}

function handleModalClose() {
  activeVotingGauge.value = null;
  refetchVotingGauges.value();
}

function handleVoteSuccess() {
  refetchVotingGauges.value();
}

return (_ctx: any,_cache: any) => {
  const _component_BalTooltip = _resolveComponent("BalTooltip")!
  const _component_BalCard = _resolveComponent("BalCard")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.$t('veBAL.liquidityMining.title')), 1),
        _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t('veBAL.liquidityMining.description')), 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_component_BalCard, {
          shadow: "none",
          class: "min-w-max md:w-48"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _hoisted_7,
              _createVNode(_component_BalTooltip, {
                text: _ctx.$t('veBAL.liquidityMining.myUnallocatedVotesTooltip'),
                iconClass: "text-gray-400 dark:text-gray-600",
                iconSize: "sm",
                width: "72",
                class: "mt-1"
              }, null, 8, ["text"])
            ]),
            _createElementVNode("p", {
              class: _normalizeClass(["mr-1 inline text-lg font-semibold", { 'text-red-500': _unref(hasExpiredLock) }])
            }, [
              (_unref(hasLock))
                ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_unref(unallocatedVotesFormatted)), 1))
                : (_openBlock(), _createElementBlock("span", _hoisted_9, "—"))
            ], 2),
            (_unref(hasExpiredLock))
              ? (_openBlock(), _createBlock(_component_BalTooltip, {
                  key: 0,
                  text: _ctx.$t('veBAL.liquidityMining.votingPowerExpiredTooltip'),
                  iconSize: "sm",
                  iconName: 'alert-triangle',
                  iconClass: 'text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors',
                  width: "72",
                  class: "relative top-0.5"
                }, null, 8, ["text"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_component_BalCard, {
          shadow: "none",
          class: "min-w-max md:w-48"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("p", {
                class: _normalizeClass([{ 'font-medium text-orange-500': _unref(votingPeriodLastHour) }, "text-secondary mr-1 inline text-sm"])
              }, " Voting period ends ", 2),
              _createVNode(_component_BalTooltip, {
                text: _ctx.$t('veBAL.liquidityMining.votingPeriodTooltip'),
                iconSize: "sm",
                iconClass: "text-gray-400 dark:text-gray-600",
                width: "72",
                class: "mt-1"
              }, null, 8, ["text"])
            ]),
            _createElementVNode("p", _hoisted_11, [
              (_unref(votingPeriodEnd).length)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: _normalizeClass({ 'text-orange-500': _unref(votingPeriodLastHour) })
                  }, _toDisplayString(_ctx.$t('veBAL.liquidityMining.votingPeriodCountdown', _unref(votingPeriodEnd))), 3))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(GaugesTable, {
      key: _unref(votingGauges) && _unref(isLoading),
      expiredGauges: _unref(expiredGauges),
      isLoading: _unref(isLoading),
      data: _unref(votingGauges),
      noPoolsLabel: _ctx.$t('noInvestments'),
      showPoolShares: "",
      class: "mb-8",
      onClickedVote: setActiveGaugeVote
    }, null, 8, ["expiredGauges", "isLoading", "data", "noPoolsLabel"]),
    (_openBlock(), _createBlock(_Teleport, { to: "#modal" }, [
      (!!activeVotingGauge.value)
        ? (_openBlock(), _createBlock(GaugeVoteModal, {
            key: 0,
            gauge: activeVotingGauge.value,
            logoURIs: orderedTokenURIs(activeVotingGauge.value),
            poolURL: 
        _unref(poolURLFor)(activeVotingGauge.value.pool.id, activeVotingGauge.value.network)
      ,
            unallocatedVoteWeight: _unref(unallocatedVoteWeight),
            veBalLockInfo: _unref(veBalLockInfoQuery).data,
            onSuccess: handleVoteSuccess,
            onClose: handleModalClose
          }, null, 8, ["gauge", "logoURIs", "poolURL", "unallocatedVoteWeight", "veBalLockInfo"]))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})